import {
  useState,
  useCallback,
  useEffect,
  useMemo } from "react";
import '@xyflow/react/dist/style.css';

import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "../contexts/SnackBarContext";

import {
    Box,
  Button,
  Typography,
  DialogActions,
  DialogTitle,
  Dialog,
  IconButton,
  Pagination,
  TextField,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  CircularProgress,
  Tooltip
} from "@mui/material";
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Select from './SearchableSelect';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import {
    aggiornaComanda,
    spostaProdottiComanda,
    eliminaComanda,
    getAllOrdini,
    patchProdottoComanda,
    patchPacchettoComanda,
    trasferisciProdotti,
    getKitchenMarches,
    recordKitchenMarches,
} from "../APIs/OrdersAPI";
import { printDocument } from "../APIs/PrintersAPI";
import { aggiornaTavolo, releaseTableSession } from "../APIs/TablesAPI";
import { getAllPrenotazioni } from "../APIs/ReservationsAPI";
import { SetupWSEventListener } from "../utils/setupWSEventListener";
import { Notes } from "@mui/icons-material";
import { ModalNotes } from "./modalNotes";
import { buildKitchenTargets, normalizeKitchenLines } from "../utils/kitchenWorkflow";
import { getTenantSettings } from "../APIs/TenantSettingsAPI";

const isTastingMenuPackage = (orderPackage) => (
    orderPackage?.package?.type === "TASTING_MENU"
    || orderPackage?.packageType === "TASTING_MENU"
);

const getPackageSourceItems = (orderPackage) => {
    if (Array.isArray(orderPackage?.packageItems)) return orderPackage.packageItems;
    if (isTastingMenuPackage(orderPackage) && Array.isArray(orderPackage?.items) && orderPackage.items.length > 0) {
        return orderPackage.items;
    }
    return Array.isArray(orderPackage?.package?.items) ? orderPackage.package.items : [];
};

const getPackageItemCourse = (item) => ({
    index: Math.max(1, Math.trunc(Number(item?.courseIndex || 1) || 1)),
    name: String(item?.courseName || "").trim(),
});

const getPackageItemName = (item) => item?.productName || item?.product?.name || "-";
const getPackageItemVariantName = (item) => item?.variantName || item?.variant?.name || "";

const compareOrdersByCourse = (a, b) => {
    const aTime = new Date(a?.createdAt).getTime();
    const bTime = new Date(b?.createdAt).getTime();
    if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) return aTime - bTime;
    if (Number.isFinite(aTime) !== Number.isFinite(bTime)) return Number.isFinite(aTime) ? -1 : 1;
    return Number(a?.id || 0) - Number(b?.id || 0);
};

const orderBelongsToTable = (order, tableId) => {
    const normalizedTableId = Number(tableId);
    if (!Number.isInteger(normalizedTableId) || normalizedTableId <= 0) return false;
    if (Number(order?.tableId ?? order?.table?.id) === normalizedTableId) return true;
    return (Array.isArray(order?.orderTables) ? order.orderTables : []).some((link) => (
        Number(link?.tableId ?? link?.table?.id) === normalizedTableId
    ));
};

const buildTastingCourseRows = (packageLines) => {
    const courses = new Map();

    (Array.isArray(packageLines) ? packageLines : [])
        .filter((line) => isTastingMenuPackage(line) && Number(line?.quantity || 0) > 0)
        .forEach((line) => {
            const packageQuantity = Number(line.quantity || 0);
            const packageName = line?.package?.name || line?.name || "-";

            getPackageSourceItems(line).forEach((item) => {
                const course = getPackageItemCourse(item);
                if (!courses.has(course.index)) {
                    courses.set(course.index, { name: course.name, items: new Map() });
                } else if (!courses.get(course.index).name && course.name) {
                    courses.get(course.index).name = course.name;
                }

                const variantName = getPackageItemVariantName(item);
                const itemKey = [
                    item?.productId ?? getPackageItemName(item),
                    item?.variantId ?? variantName,
                ].join(":");
                const courseItems = courses.get(course.index).items;
                const multipliedQuantity = Number(item?.quantity || 0) * packageQuantity;

                if (!courseItems.has(itemKey)) {
                    courseItems.set(itemKey, {
                        name: getPackageItemName(item),
                        variantName,
                        quantity: 0,
                        packageNames: new Set(),
                        lineRefs: [],
                    });
                }

                const aggregated = courseItems.get(itemKey);
                aggregated.quantity += multipliedQuantity;
                aggregated.packageNames.add(packageName);
                if (item?.id && line?._orderId) {
                    aggregated.lineRefs.push({
                        orderId: Number(line._orderId),
                        lineKey: `package-item:${item.id}`,
                    });
                }
            });
        });

    return new Map(
        [...courses.entries()].map(([courseIndex, course]) => [
            courseIndex,
            {
                name: course.name,
                items: [...course.items.values()].map((item) => ({
                    ...item,
                    packageNames: [...item.packageNames],
                })),
            },
        ])
    );
};

const buildFixedPriceCourseRows = (menuLines) => {
    const courses = new Map();

    (Array.isArray(menuLines) ? menuLines : []).forEach((menuLine) => {
        const menuQuantity = Math.max(1, Number(menuLine?.quantity || 1));
        (Array.isArray(menuLine?.selections) ? menuLine.selections : []).forEach((selection) => {
            const courseIndex = Math.max(1, Math.trunc(Number(selection?.courseIndex || 1) || 1));
            const courseName = String(selection?.groupNameSnapshot || "").trim();
            if (!courses.has(courseIndex)) {
                courses.set(courseIndex, { name: courseName, items: [] });
            } else if (!courses.get(courseIndex).name && courseName) {
                courses.get(courseIndex).name = courseName;
            }
            courses.get(courseIndex).items.push({
                id: selection.id,
                name: selection.productNameSnapshot || selection?.product?.name || "-",
                quantity: Number(selection.quantity || 1) * menuQuantity,
                extraPrice: Number(selection.extraPriceSnapshot || 0),
                menuName: menuLine.menuNameSnapshot || "Menu",
                lineRefs: selection?.id && menuLine?._orderId
                    ? [{ orderId: Number(menuLine._orderId), lineKey: `fixed:${selection.id}` }]
                    : [],
            });
        });
    });

    return courses;
};

const hasVisibleOrderRows = (order) => {
    const hasProducts = (Array.isArray(order?.orderProducts) ? order.orderProducts : [])
        .some((row) => Number(row?.quantity || 0) > 0);
    const hasPackages = (Array.isArray(order?.orderPackages) ? order.orderPackages : [])
        .some((row) => Number(row?.quantity || 0) > 0);
    const hasFixedMenus = (Array.isArray(order?.orderFixedPriceMenus) ? order.orderFixedPriceMenus : [])
        .some((row) => Number(row?.quantity || 0) > 0);
    return hasProducts || hasPackages || hasFixedMenus;
};

const DettaglioComande = ({
    open,
    tavoloId,
    tavoli,
    categorie,
    sottoCategorie,
    liveDraftItems = [],
    onDeleteLiveDraftItem,
    onIncrementLiveDraftItem,
    onDecrementLiveDraftItem,
    onEditLiveDraftNote,
    onEditLiveDraftVariants,
    mobileExpanded = false,
    selectedTargetCourseId = 'NEW',
    onSelectTargetCourse,
    onPersistedOrderChanged,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const isRestaurantTenant = String(localStorage.getItem('tenantType') || '').toUpperCase() === 'RISTORANTE';
    const [currentPage, setCurrentPage] = useState(null);
    const [openAccordions, setOpenAccordions] = useState([]);
    const tableValue = []
    const [openDelete, setOpenDelete] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);
    const [openNote, setOpenNote] = useState(false);
    const [prodottoNote, setProdottoNote] = useState({});
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orders, setOrders] = useState(null);
    const [reservation, setReservation] = useState(null);
    const [showFixedMenuNameOnKitchenPrint, setShowFixedMenuNameOnKitchenPrint] = useState(false);
    const [orderPrintFontSize, setOrderPrintFontSize] = useState('NORMAL');

    const [openEdit, setOpenEdit] = useState(false)
    const [editOrder, setEditOrder] = useState(null)
    const [editPriceOrder, setEditPriceOrder] = useState(null)
    const [editQtyOrder, setEditQtyOrder] = useState(null)

    const [trasferisciMode, setTrasferisciMode] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState(new Set());
    const [openTrasferisci, setOpenTrasferisci] = useState(false);
    const [openOccupiedTransferConfirm, setOpenOccupiedTransferConfirm] = useState(false);
    const [destTableId, setDestTableId] = useState('');
    const [isTransferring, setIsTransferring] = useState(false);
    const [marchedKeys, setMarchedKeys] = useState(new Set());
    const [marchingKeys, setMarchingKeys] = useState(new Set());

    const fetchData = useCallback(async () => {
        try {
            const ordersData = await getAllOrdini(token, tenant, { noCache: true });
            setOrders(ordersData);
            const reservationData = await getAllPrenotazioni(token, tenant);
            setReservation(reservationData);
        } catch (error) {
            showSnackbar(`${t('reservations_details.error')}: ${error}`, "error");
        }
    }, [token, tenant, t, showSnackbar]);

    useEffect(() => {
        fetchData();
        const cleanup = SetupWSEventListener(fetchData);
        return cleanup;
    }, [fetchData]);

    useEffect(() => {
        const handler = () => fetchData();
        window.addEventListener('comanda-creata', handler);
        return () => window.removeEventListener('comanda-creata', handler);
    }, [fetchData]);

    const getPrenotazioneImminente = useCallback((tableId) => {
        const oggi = new Date();
        oggi.setHours(0, 0, 0, 0);
        const domani = new Date(oggi);
        domani.setDate(domani.getDate() + 1);

        const prenotazioniTavoloOggi = reservation?.filter(p => {
            const dataPrenotazione = new Date(p.startDate);
            const belongsToTable = p.tableReservations?.some(tr => tr.tableId === tableId || tr.table?.id === tableId);
            const isConfirmed = p.status === 'CONFIRMED';
            return isConfirmed && belongsToTable &&
                dataPrenotazione >= oggi &&
                dataPrenotazione < domani;
        });

        if (prenotazioniTavoloOggi?.length === 0) return null;
        return prenotazioniTavoloOggi?.sort((a, b) => new Date(a.startDate) - new Date(b.endDate))[0];
    }, [reservation]);

    const tavoliDettagliati = useMemo(() => {
        return tavoli.map(table => {
            const prenotazione = getPrenotazioneImminente(table.id);
            const ordiniTavolo = orders?.filter(o => orderBelongsToTable(o, table.id));

            const prodottiAggregati = ordiniTavolo?.flatMap(o => o.orderProducts || [])
                .reduce((acc, prodotto) => {
                    const key = prodotto.productId;
                    if (!acc[key]) {
                        acc[key] = {
                            ...(prodotto.product || {}),
                            id: prodotto.productId,
                            name: prodotto.product?.name || t("new_product"),
                            quantity: prodotto.quantity,
                        };
                    } else {
                        acc[key].quantity += prodotto.quantity;
                    }
                    return acc;
                }, {});

            const clientName = [
                prenotazione?.client?.name,
                prenotazione?.client?.surname,
            ].filter(Boolean).join(" ").trim();

            return {
                ...table,
                prenotazione,
                hasPrenotazione: !!prenotazione,
                nomeCliente: prenotazione
                    ? clientName || t("client")
                    : '—',
                oraPrenotazione: prenotazione
                    ? new Date(prenotazione.startDate).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', hour12: false })
                    : '—',
                ordersCreatedAt: ordiniTavolo?.filter(ord => ord.createdAt),
                numeroOspiti: prenotazione ? prenotazione.guests : '—',
                notePrenotazione: prenotazione ? prenotazione.note : null,
                clientId: prenotazione ? prenotazione.clientId : null,
                ordini: Object.values(prodottiAggregati ? prodottiAggregati : []),
            };
        });
    }, [tavoli, orders, getPrenotazioneImminente, t]);

    const tavolo = useMemo(() => {
        return tavoliDettagliati?.find(t => t.id === tavoloId)
    }, [tavoliDettagliati, tavoloId]);

    const tavoloFiltrato = useMemo(() => {
        const today = formatDate(new Date());
        return (tavolo?.ordersCreatedAt || []).filter((ord) => {
            if (['PAID', 'CANCELLED'].includes(ord?.status) || formatDate(ord.createdAt) !== today) return false;
            return hasVisibleOrderRows(ord);
        }).sort(compareOrdersByCourse);
    }, [tavolo]);

    const visualCourses = useMemo(() => {
        const tastingCourses = buildTastingCourseRows(
            tavoloFiltrato.flatMap((order) => (order?.orderPackages || []).map((row) => ({
                ...row,
                _orderId: order.id,
            })))
        );
        const fixedPriceCourses = buildFixedPriceCourseRows(
            tavoloFiltrato.flatMap((order) => (order?.orderFixedPriceMenus || []).map((row) => ({
                ...row,
                _orderId: order.id,
            })))
        );
        const maxTastingCourse = Math.max(0, ...tastingCourses.keys());
        const maxFixedPriceCourse = Math.max(0, ...fixedPriceCourses.keys());
        const totalCourses = Math.max(tavoloFiltrato.length, maxTastingCourse, maxFixedPriceCourse);

        return Array.from({ length: totalCourses }, (_, index) => {
            const courseIndex = index + 1;
            const order = tavoloFiltrato[index] || null;
            const tastingCourse = tastingCourses.get(courseIndex);
            const fixedPriceCourse = fixedPriceCourses.get(courseIndex);
            return {
                id: order?.id ?? `virtual-course-${courseIndex}`,
                courseIndex,
                courseName: fixedPriceCourse?.name || tastingCourse?.name || "",
                order,
                orderIndex: order ? index : -1,
                tastingItems: tastingCourse?.items || [],
                fixedPriceItems: fixedPriceCourse?.items || [],
            };
        });
    }, [tavoloFiltrato]);

    useEffect(() => {
        let cancelled = false;
        getTenantSettings(token, tenant)
            .then((settings) => {
                if (!cancelled) {
                    setShowFixedMenuNameOnKitchenPrint(settings?.showFixedMenuNameOnKitchenPrint === true);
                    setOrderPrintFontSize(settings?.orderPrintFontSize || 'NORMAL');
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [token, tenant]);

    const kitchenLines = useMemo(() => normalizeKitchenLines(tavoloFiltrato, {
        showFixedMenuName: showFixedMenuNameOnKitchenPrint,
        roomId: tavolo?.roomId ?? tavolo?.room?.id,
    }), [tavoloFiltrato, showFixedMenuNameOnKitchenPrint, tavolo?.roomId, tavolo?.room?.id]);
    const marchKey = useCallback((line) => `${line.orderId}:${line.key}`, []);

    useEffect(() => {
        const orderIds = tavoloFiltrato.map((order) => Number(order.id)).filter(Boolean);
        if (orderIds.length === 0) {
            setMarchedKeys(new Set());
            return;
        }
        let cancelled = false;
        getKitchenMarches(orderIds, token, tenant)
            .then((entries) => {
                if (!cancelled) {
                    setMarchedKeys(new Set((entries || []).map((entry) => `${entry.orderId}:${entry.lineKey}`)));
                }
            })
            .catch((error) => {
                if (!cancelled) showSnackbar(error?.message || 'Errore nel recupero delle marce', 'error');
            });
        return () => { cancelled = true; };
    }, [tavoloFiltrato, token, tenant, showSnackbar]);

    const handleMarchLines = useCallback(async (requestedLines, scope) => {
        const pending = (requestedLines || []).filter((line) => (
            Number(line?.courseIndex) > 1 && !marchedKeys.has(marchKey(line))
        ));
        if (pending.length === 0) {
            showSnackbar('Gli articoli selezionati sono gia stati messi in marcia', 'info');
            return;
        }

        const pendingKeys = pending.map(marchKey);
        setMarchingKeys((current) => new Set([...current, ...pendingKeys]));
        try {
            const printable = pending.map((line) => ({
                ...line,
                displayLine: `MARCIA: ${line.printLine}`,
            }));
            const { targets, unassignedItems } = buildKitchenTargets(printable, {
                title: scope === 'course' ? 'MARCIA PORTATA' : 'MARCIA ARTICOLO',
                tableName: tavolo?.name,
                tableNote: tavolo?.note,
                customerLabel: tavolo?.nomeCliente,
                fontSize: orderPrintFontSize,
            });
            if (unassignedItems.length > 0) {
                throw new Error(`Associa una stampante a: ${unassignedItems.slice(0, 5).join(', ')}`);
            }
            if (targets.length === 0) throw new Error('Nessuna stampante configurata per gli articoli selezionati');

            await printDocument({ targets, action: 'print_march' }, token, tenant);
            const saved = await recordKitchenMarches(
                pending.map((line) => ({ orderId: line.orderId, lineKey: line.key })),
                token,
                tenant
            );
            setMarchedKeys((current) => new Set([
                ...current,
                ...(saved || []).map((entry) => `${entry.orderId}:${entry.lineKey}`),
            ]));
            showSnackbar(scope === 'course' ? 'Portata messa in marcia' : 'Articolo messo in marcia', 'success');
        } catch (error) {
            showSnackbar(error?.message || 'Errore durante la marcia', 'error');
        } finally {
            setMarchingKeys((current) => {
                const next = new Set(current);
                pendingKeys.forEach((key) => next.delete(key));
                return next;
            });
        }
    }, [marchedKeys, marchKey, orderPrintFontSize, showSnackbar, tavolo, tenant, token]);

    const linesForRefs = useCallback((refs) => {
        const keys = new Set((refs || []).map((ref) => `${ref.orderId}:${ref.lineKey}`));
        return kitchenLines.filter((line) => keys.has(marchKey(line)));
    }, [kitchenLines, marchKey]);

    const renderMarchButton = (lines, scope = 'item') => {
        const available = (lines || []).filter(Boolean);
        if (available.length > 0 && available.every((line) => Number(line.courseIndex) === 1)) {
            return null;
        }
        const completed = available.length > 0 && available.every((line) => marchedKeys.has(marchKey(line)));
        const loading = available.some((line) => marchingKeys.has(marchKey(line)));

        const handleClick = (event) => {
            event.stopPropagation();
            handleMarchLines(available, scope);
        };

        if (scope === 'course') {
            return (
                <Button
                    size="small"
                    variant={completed ? "outlined" : "contained"}
                    color={completed ? "success" : "primary"}
                    disabled={available.length === 0 || completed || loading}
                    onClick={handleClick}
                    startIcon={loading
                        ? <CircularProgress size={14} color="inherit" />
                        : completed ? <CheckRoundedIcon /> : <PlayArrowRoundedIcon />}
                    sx={{
                        minWidth: 0,
                        minHeight: 30,
                        px: 1.25,
                        py: 0.25,
                        borderRadius: 999,
                        whiteSpace: 'nowrap',
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: '0.03em',
                        boxShadow: 'none',
                        '& .MuiButton-startIcon': { mr: 0.4 },
                        '&.Mui-disabled': completed ? {
                            color: theme.palette.success.main,
                            borderColor: theme.palette.success.main,
                        } : undefined,
                    }}
                >
                    {completed ? 'IN MARCIA' : 'MARCIA PORTATA'}
                </Button>
            );
        }

        const tooltipLabel = completed ? 'Articolo gia in marcia' : 'Metti in marcia questo articolo';
        return (
            <Tooltip title={tooltipLabel} arrow>
                <span>
                    <IconButton
                        size="small"
                        aria-label={tooltipLabel}
                        disabled={available.length === 0 || completed || loading}
                        onClick={handleClick}
                        sx={{
                            width: 30,
                            height: 30,
                            flexShrink: 0,
                            border: '1px solid',
                            borderColor: completed ? 'success.main' : 'primary.main',
                            color: completed ? 'success.main' : 'primary.main',
                            backgroundColor: completed ? 'success.main' : 'primary.main',
                            backgroundImage: completed
                                ? 'linear-gradient(rgba(255,255,255,0.88), rgba(255,255,255,0.88))'
                                : 'linear-gradient(rgba(255,255,255,0.90), rgba(255,255,255,0.90))',
                            transition: 'transform 140ms ease, background-color 140ms ease',
                            '&:hover': {
                                backgroundColor: completed ? 'success.main' : 'primary.main',
                                transform: 'translateY(-1px)',
                            },
                            '&.Mui-disabled': {
                                color: completed ? theme.palette.success.main : theme.palette.action.disabled,
                                borderColor: completed ? theme.palette.success.main : theme.palette.action.disabledBackground,
                            },
                        }}
                    >
                        {loading
                            ? <CircularProgress size={15} color="inherit" />
                            : completed ? <CheckRoundedIcon fontSize="small" /> : <PlayArrowRoundedIcon fontSize="small" />}
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    useEffect(() => {
        if (visualCourses.length > 0 && visualCourses.length <= 6) {
            setOpenAccordions(visualCourses.map((course) => course.id));
        } else {
            setOpenAccordions([]);
        }
    }, [visualCourses]);

    useEffect(() => {
        setOpenDelete(false);
        setIdToDelete(null);
        setSelectedOrder(null);
    }, [tavoloId]);

    function matchCategory(categorie, idCategoria) {
        return categorie.find(cat => Number(cat.id) === Number(idCategoria))?.name
    }

    function getProductCategoryLabel(product) {
        const categoryEntries = Array.isArray(product?.categories) ? product.categories : [];
        const labels = categoryEntries
            .map((entry) => {
                const categoryId = Number(entry?.categoryId ?? entry?.category?.id);
                const subcategoryId = Number(entry?.subcategoryId ?? entry?.subcategory?.id);
                const categoryName = entry?.category?.name || matchCategory(categorie, categoryId);
                const subcategoryName = entry?.subcategory?.name || matchCategory(sottoCategorie, subcategoryId);

                if (categoryName && subcategoryName) return `${categoryName} - ${subcategoryName}`;
                return categoryName || subcategoryName || null;
            })
            .filter(Boolean);

        if (labels.length > 0) return [...new Set(labels)].join(", ");

        if (product?.categoryId) {
            const categoryName = matchCategory(categorie, Number(product.categoryId));
            const subcategoryName = product?.subcategoryId
                ? matchCategory(sottoCategorie, Number(product.subcategoryId))
                : null;
            if (categoryName && subcategoryName) return `${categoryName} - ${subcategoryName}`;
            if (categoryName) return categoryName;
        }

        return t("NoCategory");
    }

    function packageItemsLabel(orderPackage) {
        const isTasting = orderPackage?.package?.type === "TASTING_MENU";
        const sourceItems = isTasting && Array.isArray(orderPackage?.items) && orderPackage.items.length > 0
            ? orderPackage.items
            : orderPackage?.package?.items;
        if (!Array.isArray(sourceItems) || sourceItems.length === 0) return "-";

        return [...sourceItems]
            .sort((a, b) => {
                const courseDiff = Number(a?.courseIndex || 1) - Number(b?.courseIndex || 1);
                if (courseDiff !== 0) return courseDiff;
                return Number(a?.sortOrder ?? 0) - Number(b?.sortOrder ?? 0);
            })
            .map((item) => {
                const name = item?.productName || item?.product?.name || t("new_product");
                const variantName = item?.variantName || item?.variant?.name || "";
                const qty = Number(item?.quantity || 1);
                const courseIndex = Math.max(1, Number(item?.courseIndex || 1));
                const courseName = String(item?.courseName || "").trim();
                const coursePrefix = isTasting
                    ? `${courseIndex}${courseName ? ` ${courseName}` : ""}: `
                    : "";
                return `${coursePrefix}${name}${variantName ? ` + ${variantName}` : ""} x${qty}`;
            })
            .join(", ");
    }

    function getVariantIds(orderProduct) {
        if (Array.isArray(orderProduct?.variantIds)) {
            return orderProduct.variantIds
                .map((id) => Number(id))
                .filter((id) => Number.isInteger(id) && id > 0);
        }
        if (Array.isArray(orderProduct?.orderProductVariants)) {
            return orderProduct.orderProductVariants
                .map((opv) => Number(opv?.variantId ?? opv?.variant?.id))
                .filter((id) => Number.isInteger(id) && id > 0);
        }
        const single = Number(orderProduct?.variantId ?? orderProduct?.variant?.id);
        return Number.isInteger(single) && single > 0 ? [single] : [];
    }

    function getVariantNames(orderProduct) {
        if (!Array.isArray(orderProduct?.orderProductVariants)) return [];
        return orderProduct.orderProductVariants
            .map((opv) => opv?.variant?.name)
            .filter(Boolean);
    }

    function formatQuantityWithUnit(quantity, product) {
        const qty = Number(quantity || 0);
        if (!product?.pricePerWeight) return `${qty}`;
        const unit = product?.typeOfWeight === 'HECTOGRAM' ? 'hg' : 'kg';
        return `${qty}${unit}`;
    }

    function getOrderProductName(orderProduct) {
        return orderProduct?.product?.name || orderProduct?.productName || t("new_product");
    }

    function isSameProductLine(a, b) {
        if (Number(a?.productId) !== Number(b?.productId)) return false;
        if (a?.product?.pricePerWeight || b?.product?.pricePerWeight) return false;
        const aIds = getVariantIds(a).sort((x, y) => x - y);
        const bIds = getVariantIds(b).sort((x, y) => x - y);
        if (aIds.length !== bIds.length) return false;
        return aIds.every((id, idx) => id === bIds[idx]);
    }

    const mapProductForOrderUpdate = (product) => ({
        ...(Number.isInteger(Number(product?.id)) ? { id: Number(product.id) } : {}),
        productId: Number(product.productId ?? product.product?.id),
        variantIds: getVariantIds(product),
        quantity: Number(product.quantity) || 0,
        ...(product?.discount !== undefined && product?.discount !== null ? { discount: Number(product.discount) } : {}),
        ...(typeof product?.note === "string" ? { note: product.note } : {}),
    });

    const mapPackageForOrderUpdate = (orderPackage) => ({
        ...(Number.isInteger(Number(orderPackage?.id)) ? { id: Number(orderPackage.id) } : {}),
        packageId: Number(orderPackage.packageId ?? orderPackage.package?.id),
        quantity: Number(orderPackage.quantity) || 0,
        ...(orderPackage?.discount !== undefined && orderPackage?.discount !== null ? { discount: Number(orderPackage.discount) } : {}),
        ...(typeof orderPackage?.note === "string" ? { note: orderPackage.note } : {}),
    });

    const buildOrderUpdatePayload = (order, products) => ({
        tableId: Number(order.tableId ?? order.table?.id),
        ...(order.clientId != null ? { clientId: Number(order.clientId) } : {}),
        totalDiscount: Number(order.totalDiscount || 0),
        status: order.status || "OPEN",
        products: (Array.isArray(products) ? products : [])
            .map(mapProductForOrderUpdate)
            .filter((p) => Number.isInteger(p.productId) && p.productId > 0 && p.quantity > 0),
        packages: (Array.isArray(order.orderPackages) ? order.orderPackages : [])
            .map(mapPackageForOrderUpdate)
            .filter((p) => Number.isInteger(p.packageId) && p.packageId > 0 && p.quantity > 0),
    });

    function handleChangePage(_, value) {
        setCurrentPage(value)
        const idList = tableValue[value - 1].id
        setOpenAccordions([idList]);
    }

    const toggleAccordion = (id) => {
        setOpenAccordions((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
        setCurrentPage(null)
    };

    const releaseTableIfEmpty = useCallback(async (remainingOrders) => {
        const normalizedTableId = Number(tavoloId);
        if (!Number.isInteger(normalizedTableId) || normalizedTableId <= 0) return;
        if ((Array.isArray(remainingOrders) ? remainingOrders : []).some(hasVisibleOrderRows)) return;

        const nextStatus = tavolo?.hasPrenotazione ? "RISERVATO" : "LIBERO";
        await aggiornaTavolo(normalizedTableId, { status: nextStatus, takenSeats: 0 }, token, tenant);
        await releaseTableSession(normalizedTableId, token, tenant).catch(() => {});
    }, [tavolo?.hasPrenotazione, tavoloId, token, tenant]);

    function filtraProdottiOrdini(tav) {
        const firstMatching = tav
            .map((ordiniTavolo) =>
                ordiniTavolo.orderProducts.filter(
                    (ordProd) => ordProd.orderId === selectedOrder && ordProd.id !== idToDelete
                )
            )
            .find((ordProdFilt) => ordProdFilt.length > 0);
        return firstMatching || [];
    }

    const handleMoveProduct = async (ordineCorrenteIndex, product, direction) => {
        const ordiniVisibili = tavoloFiltrato;
        const ordineOrigine = ordiniVisibili[ordineCorrenteIndex];
        const ordineDestinazioneIndex = direction === 'up'
            ? ordineCorrenteIndex - 1
            : ordineCorrenteIndex + 1;
        const ordineDestinazione = ordiniVisibili[ordineDestinazioneIndex];

        if (!ordineOrigine || !ordineDestinazione) return;

        try {
            const prodottiOrigineAggiornati = ordineOrigine.orderProducts
                .filter((p) => Number(p.id) !== Number(product.id));

            const pacchettiOrigine = Array.isArray(ordineOrigine.orderPackages) ? ordineOrigine.orderPackages : [];

            if (prodottiOrigineAggiornati.length === 0 && pacchettiOrigine.length === 0) {
                await eliminaComanda(ordineOrigine.id, token, tenant);
            } else {
                await spostaProdottiComanda(
                    ordineOrigine.id,
                    {
                        orderProducts: prodottiOrigineAggiornati.map(mapProductForOrderUpdate),
                        orderPackages: pacchettiOrigine.map(mapPackageForOrderUpdate),
                    },
                    token,
                    tenant
                );
            }

            const prodottoEsistente = ordineDestinazione.orderProducts.find((p) => isSameProductLine(p, product));

            let prodottiDestinazioneAggiornati;
            if (prodottoEsistente) {
                prodottiDestinazioneAggiornati = ordineDestinazione.orderProducts.map(p =>
                    isSameProductLine(p, product)
                        ? { ...p, quantity: p.quantity + product.quantity }
                        : p
                );
            } else {
                prodottiDestinazioneAggiornati = [
                    ...ordineDestinazione.orderProducts,
                    {
                        productId: product.productId,
                        variantIds: getVariantIds(product),
                        quantity: product.quantity,
                        ...(product?.unitPrice !== undefined && product?.unitPrice !== null ? { unitPrice: Number(product.unitPrice) } : {}),
                        ...(product?.discount !== undefined && product?.discount !== null ? { discount: Number(product.discount) } : {}),
                        ...(typeof product?.note === "string" ? { note: product.note } : {}),
                    },
                ];
            }

            await aggiornaComanda(
                ordineDestinazione.id,
                buildOrderUpdatePayload(ordineDestinazione, prodottiDestinazioneAggiornati),
                token,
                tenant
            );
            await fetchData();
            window.dispatchEvent(new CustomEvent('comanda-creata'));
        } catch (error) {
            handleAlert(t('orderDetails_alert.errorMoveProduct') + error, "error")
        }
    };

    const handleDelete = async (tavoloFil) => {
        const ordineCorrente = (Array.isArray(tavoloFil) ? tavoloFil : [])
            .find((ordine) => Number(ordine.id) === Number(selectedOrder));
        const pacchettiCorrente = Array.isArray(ordineCorrente?.orderPackages)
            ? ordineCorrente.orderPackages
            : [];
        const fixedMenusCorrente = Array.isArray(ordineCorrente?.orderFixedPriceMenus)
            ? ordineCorrente.orderFixedPriceMenus
            : [];
        const updateOrder = filtraProdottiOrdini(tavoloFil)?.map(
            (product) => {
                return mapProductForOrderUpdate(product)
            }
        )

        if (updateOrder.length < 1 && pacchettiCorrente.length < 1 && fixedMenusCorrente.length < 1) {
            try {
                await eliminaComanda(selectedOrder, token, tenant);
                const remainingOrders = (Array.isArray(tavoloFil) ? tavoloFil : [])
                    .filter((ordine) => Number(ordine.id) !== Number(selectedOrder));
                await releaseTableIfEmpty(remainingOrders);
                window.dispatchEvent(new CustomEvent('comanda-creata'));
                await fetchData();
                setSelectedOrder(null)
                setIdToDelete(null)
            } catch (error) {
                handleAlert(t('orderDetails_alert.errorDeleteOrder') + error, "error")
            }
        } else {
            try {
                await spostaProdottiComanda(
                    selectedOrder,
                    {
                        orderProducts: updateOrder,
                        orderPackages: pacchettiCorrente.map(mapPackageForOrderUpdate),
                    },
                    token,
                    tenant
                );
                await fetchData();
                window.dispatchEvent(new CustomEvent('comanda-creata'));
                setSelectedOrder(null)
                setIdToDelete(null)
            } catch (error) {
                handleAlert(t('orderDetails_alert.errorPatchOrder') + error, "error")
            }
        }
    };

    const handleChangeProductNote = async (orderProductId, value) => {
        const sourceOrder = tavoloFiltrato.find((order) =>
            (order?.orderProducts || []).some(
                (product) => Number(product.id) === Number(orderProductId)
            )
        );
        const orderId = Number(prodottoNote?.orderId ?? sourceOrder?.id);

        if (!Number.isInteger(orderId) || orderId <= 0) {
            const error = new Error('Comanda non trovata per la modifica della nota');
            showSnackbar(error.message, 'error');
            throw error;
        }

        try {
            await patchProdottoComanda(orderId, {
                orderProductId: Number(orderProductId),
                note: String(value || ''),
            }, token, tenant);
            await Promise.all([
                fetchData(),
                typeof onPersistedOrderChanged === 'function'
                    ? onPersistedOrderChanged()
                    : Promise.resolve(),
            ]);
            window.dispatchEvent(new CustomEvent('comanda-creata'));
            showSnackbar('Nota aggiornata', 'success');
        } catch (error) {
            showSnackbar(error?.message || 'Errore durante la modifica della nota', 'error');
            throw error;
        }
    };

    const handleTrasferisci = async () => {
        if (!destTableId || selectedProductIds.size === 0) return;
        setIsTransferring(true);
        try {
            const allProducts = tavoloFiltrato.flatMap(o => o.orderProducts || []);
            const selected = allProducts.filter(p => selectedProductIds.has(p.id));

            await trasferisciProdotti({
  sourceTableId: Number(tavoloId),
  destinationTableId: Number(destTableId),
  items: selected.map(p => ({
    type: 'product',
    orderId: Number(p.orderId),
    orderProductId: Number(p.id),
  })),
}, token, tenant);

            await Promise.all([
                fetchData(),
                typeof onPersistedOrderChanged === 'function'
                    ? onPersistedOrderChanged()
                    : Promise.resolve(),
            ]);
            window.dispatchEvent(new CustomEvent('comanda-creata'));
            setOpenTrasferisci(false);
            setTrasferisciMode(false);
            setSelectedProductIds(new Set());
            setDestTableId('');
            showSnackbar(t('miscUi.transferSuccess'), 'success');
        } catch (err) {
            handleAlert('Errore trasferimento: ' + err.message, 'error');
        } finally {
            setIsTransferring(false);
        }
    };

    const handleConfirmTrasferisci = () => {
        const destination = tavoli.find((tb) => Number(tb.id) === Number(destTableId));
        if (destination?.status === 'OCCUPATO') {
            setOpenOccupiedTransferConfirm(true);
            return;
        }
        handleTrasferisci();
    };

    const toggleProductSelection = (productId) => {
        setSelectedProductIds(prev => {
            const next = new Set(prev);
            next.has(productId) ? next.delete(productId) : next.add(productId);
            return next;
        });
    };

    function listaOrdiniFiltrati(tavFiltrato) {
        const courses = visualCourses;
        return (
            <>
                <Box justifyItems={"anchor-center"}>
                    <Pagination
                        count={courses.length}
                        page={currentPage || 0}
                        onChange={handleChangePage}
                        color="primary"
                        variant="outlined"
                        sx={{ mt: 2, mb: 2 }}
                    />
                </Box>
                {
                    courses.map((course) => {
                        const ordine = course.order;
                        const orderIndex = course.orderIndex;
                        const isFirst = orderIndex === 0;
                        const isLast = orderIndex === tavFiltrato.length - 1;
                        const isSingle = tavFiltrato.length === 1;
                        const isDestination = ordine ? isTargetCourse(ordine.id) : false;
                        const canSelectCourse = Boolean(ordine) && canSelectTargetCourse;
                        const courseKitchenLines = kitchenLines.filter((line) => line.courseIndex === course.courseIndex);

                        const obj = { "id": course.id }
                        tableValue.push(obj)
                        return (
                            <Accordion
                                key={course.id}
                                id={course.id}
                                variant="outlined"
                                expanded={openAccordions.includes(course.id)}
                                sx={{
                                    borderRadius: 2,
                                    marginBottom: 1,
                                    minHeight: '74px',
                                    alignItems: "center",
                                    backgroundColor: isDestination ? 'rgba(212, 114, 0, 0.08)' : theme.palette.accordion.backgroundColor,
                                    border: isDestination ? `2px solid ${theme.palette.primary.main}` : theme.palette.accordion.border,
                                    boxShadow: isDestination ? '0 0 0 3px rgba(212, 114, 0, 0.12)' : 'none',
                                    cursor: canSelectCourse ? 'pointer' : 'default',
                                }}
                            >
                                <AccordionSummary
                                    onClick={() => {
                                        if (canSelectCourse) onSelectTargetCourse(ordine.id);
                                    }}
                                    sx={{ cursor: canSelectCourse ? 'pointer' : 'default' }}
                                >
                                    <Box paddingY={1.5} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <Typography
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                if (canSelectCourse) onSelectTargetCourse(ordine.id);
                                            }}
                                            sx={{ cursor: canSelectCourse ? 'pointer' : 'default' }}
                                        >
                                            {isDestination
                                                ? `${course.courseIndex} - ${t("miscUi.selectedCourse")}`
                                                : `${course.courseIndex} - ${course.courseName || t("tableMapPage.course")}`}
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {course.courseIndex > 1 && renderMarchButton(courseKitchenLines, 'course')}
                                            {isDestination && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        px: 1,
                                                        py: 0.4,
                                                        borderRadius: 999,
                                                        fontWeight: 700,
                                                        color: theme.palette.primary.main,
                                                        backgroundColor: 'rgba(212, 114, 0, 0.14)',
                                                    }}
                                                >
                                                    {t("miscUi.destination").toUpperCase()}
                                                </Typography>
                                            )}
                                            {openAccordions.includes(course.id) ?
                                                (<Typography
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        toggleAccordion(course.id);
                                                    }}
                                                    sx={{ display: "flex", alignItems: "center", gap: 0.5, color: theme.palette.accordion.color }}
                                                >
                                                    {t("tableMapPage.close").toUpperCase()}<ExpandLessIcon />
                                                </Typography>)
                                                :
                                                (<Typography
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        toggleAccordion(course.id);
                                                    }}
                                                    sx={{ display: "flex", alignItems: "center", gap: 0.5, color: theme.palette.accordion.color }}
                                                >
                                                    {t("tableMapPage.details").toUpperCase()}<ExpandMoreIcon />
                                                </Typography>)
                                            }
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {
                                        (ordine?.orderProducts || [])
                                            .filter((prodotto) => Number(prodotto?.quantity || 0) > 0)
                                            .map(
                                            (prodotto) => (
                                                <Box
                                                    key={prodotto.id || `${prodotto.orderId}-${prodotto.productId}-${getVariantIds(prodotto).join('-') || 'base'}`}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        mb: 1,
                                                        p: 1,
                                                        pt: 2,
                                                        pb: 2,
                                                        border: trasferisciMode && selectedProductIds.has(prodotto.id)
                                                            ? `2px solid ${theme.palette.primary.main}`
                                                            : theme.palette.accordion.border,
                                                        borderRadius: '4px',
                                                        backgroundColor: theme.palette.background.default,
                                                    }}
                                                >
                                                    <Box alignContent={"center"} display={"flex"} gap={1}>
                                                        {trasferisciMode && (
                                                            <Checkbox
                                                                checked={selectedProductIds.has(prodotto.id)}
                                                                onChange={() => toggleProductSelection(prodotto.id)}
                                                                onClick={e => e.stopPropagation()}
                                                                size="small"
                                                            />
                                                        )}
                                                        {!trasferisciMode && (
                                                            <Box alignContent={"center"} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                                                                <IconButton
                                                                    size='small'
                                                                    color="default"
                                                            disabled={!ordine || isFirst || isSingle}
                                                                    sx={{ p: 0.3, '& svg': { fontSize: 16 } }}
                                                                    onClick={() => { handleMoveProduct(orderIndex, prodotto, 'up') }}
                                                                >
                                                                    <KeyboardArrowUpOutlinedIcon />
                                                                </IconButton>
                                                                <IconButton
                                                                    size='small'
                                                                    color="default"
                                                                    disabled={!ordine || isLast || isSingle}
                                                                    sx={{ p: 0.3, '& svg': { fontSize: 16 } }}
                                                                    onClick={() => { handleMoveProduct(orderIndex, prodotto, 'down') }}
                                                                >
                                                                    <KeyboardArrowDownOutlinedIcon />
                                                                </IconButton>
                                                            </Box>
                                                        )}
                                                        <Box>
                                                            <Typography variant="body2" pt={'1px'} pb={'1px'} fontWeight={"bold"}>
                                                                {getProductCategoryLabel(prodotto.product)}
                                                            </Typography>
                                                            <Typography variant="body2" pt={'1px'} pb={'1px'} fontStyle={"italic"} >
                                                                (x{formatQuantityWithUnit(prodotto.quantity, prodotto.product)}){getOrderProductName(prodotto)}
                                                            </Typography>
                                                            {getVariantNames(prodotto).length > 0 && (
                                                                <Typography variant="caption" color="primary" sx={{ display: 'block', fontWeight: 800 }}>
                                                                    Varianti: {getVariantNames(prodotto).join(' + ')}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Box alignContent={"center"} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        {renderMarchButton([
                                                            kitchenLines.find((line) => line.key === `product:${prodotto.id}`)
                                                        ])}
                                                        <Typography variant="body2" padding={1} fontWeight={"bold"} alignContent={"center"}>
                                                            € {(prodotto.unitPrice * prodotto.quantity).toFixed(2)}
                                                        </Typography>
                                                        {!trasferisciMode && (
                                                            <>
                                                                <IconButton onClick={() => {
                                                                    setOpenNote(true);
                                                                    setProdottoNote(prodotto);
                                                                }}>
                                                                    <Notes />
                                                                </IconButton>
                                                                <IconButton size='small' color="default" onClick={() => {
                                                                    setOpenEdit(true)
                                                                    setEditOrder(prodotto)
                                                                    setEditPriceOrder(prodotto.unitPrice)
                                                                    setEditQtyOrder(prodotto.quantity)
                                                                }}><ModeEditOutlinedIcon /></IconButton>
                                                                <IconButton size='small' color="error" onClick={() => {
                                                                    setOpenDelete(true)
                                                                    setIdToDelete(prodotto.id)
                                                                    setSelectedOrder(prodotto.orderId)
                                                                }}><DeleteOutlineOutlinedIcon /></IconButton>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Box>
                                            )
                                        )
                                    }
                                    {
                                        (ordine?.orderPackages || [])
                                            .filter((orderPackage) => Number(orderPackage?.quantity || 0) > 0)
                                            .filter((orderPackage) => !isTastingMenuPackage(orderPackage))
                                            .map((orderPackage) => (
                                            <Box
                                                key={`pkg-${orderPackage.id}`}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    mb: 1,
                                                    p: 1,
                                                    pt: 2,
                                                    pb: 2,
                                                    border: theme.palette.accordion.border,
                                                    borderRadius: '4px',
                                                    backgroundColor: theme.palette.background.default,
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="body2" pt={'1px'} pb={'1px'} fontWeight={"bold"}>
                                                        {orderPackage.package?.type === "TASTING_MENU"
                                                            ? t("orderPackages.tastingMenu")
                                                            : t("orderPackages.label")}
                                                    </Typography>
                                                    <Typography variant="body2" pt={'1px'} pb={'1px'} fontStyle={"italic"} >
                                                        (x{orderPackage.quantity}){orderPackage.package?.name || "-"}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {packageItemsLabel(orderPackage)}
                                                    </Typography>
                                                </Box>
                                                <Box alignContent={"center"} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    {renderMarchButton([
                                                        kitchenLines.find((line) => line.key === `package:${orderPackage.id}`)
                                                    ])}
                                                    <Typography variant="body2" padding={1} fontWeight={"bold"} alignContent={"center"}>
                                                        € {((Number(orderPackage.unitPrice ?? orderPackage.package?.price ?? 0)) * Number(orderPackage.quantity || 0)).toFixed(2)}
                                                    </Typography>
                                                    {!trasferisciMode && (
                                                        <IconButton size='small' color="error" onClick={() => {
                                                            handleDeletePackage(ordine.id, orderPackage);
                                                        }}>
                                                            <DeleteOutlineOutlinedIcon />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))
                                    }
                                    {course.tastingItems.map((item, itemIndex) => (
                                        <Box
                                            key={`tasting-${course.courseIndex}-${item.name}-${item.variantName}-${itemIndex}`}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                mb: 1,
                                                p: 1,
                                                pt: 2,
                                                pb: 2,
                                                border: `1px dashed ${theme.palette.primary.main}`,
                                                borderRadius: '4px',
                                                backgroundColor: theme.palette.background.default,
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" pt={'1px'} pb={'1px'} fontWeight={"bold"}>
                                                    {t("orderPackages.tastingMenu")} - {course.courseName || `${t("tableMapPage.course")} ${course.courseIndex}`}
                                                </Typography>
                                                <Typography variant="body2" pt={'1px'} pb={'1px'} fontStyle={"italic"}>
                                                    (x{item.quantity}){item.name}
                                                    {item.variantName ? ` + ${item.variantName}` : ""}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.packageNames.join(", ")}
                                                </Typography>
                                            </Box>
                                            {renderMarchButton(linesForRefs(item.lineRefs))}
                                        </Box>
                                    ))}
                                    {course.fixedPriceItems.map((item, itemIndex) => (
                                        <Box
                                            key={`fixed-menu-${course.courseIndex}-${item.id || itemIndex}`}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                mb: 1,
                                                p: 1,
                                                pt: 2,
                                                pb: 2,
                                                border: `1px dashed ${theme.palette.warning.main}`,
                                                borderRadius: '4px',
                                                backgroundColor: theme.palette.background.default,
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {item.menuName} - {course.courseName || `${t("tableMapPage.course")} ${course.courseIndex}`}
                                                </Typography>
                                                <Typography variant="body2" fontStyle="italic">
                                                    (x{item.quantity}){item.name}
                                                </Typography>
                                                {item.extraPrice > 0 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t("fixedPriceMenu.supplementLabel", { price: item.extraPrice.toFixed(2) })}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {renderMarchButton(linesForRefs(item.lineRefs))}
                                        </Box>
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        )
                    })}

                {openNote && <ModalNotes
                    open={openNote}
                    onClose={() => {
                        setOpenNote(false);
                        setProdottoNote({});
                    }}
                    prodotto={prodottoNote}
                    note={prodottoNote.note}
                    onChange={handleChangeProductNote}
                    readOnly={false}
                />
                }
            </>
        )
    }

    const handleChangePrice = (event) => {
        const newValue = event.target.value;
        if (/^\d*\.?\d{0,2}$/.test(newValue)) {
            setEditPriceOrder(newValue);
        }
    };

    const handleChangeQty = (event) => {
        const newValue = event.target.value;
        if (/^\d*\.?\d{0,2}$/.test(newValue)) {
            setEditQtyOrder(newValue);
        }
    };

    function closeEditOrder() {
        setEditOrder(false)
        setOpenEdit(false)
        setEditPriceOrder(null)
        setEditQtyOrder(null)
    }

    function changeQty(operation) {
        if (operation === "-") if (editQtyOrder > 1) setEditQtyOrder(editQtyOrder - 1)
        if (operation === "+") setEditQtyOrder(parseInt(editQtyOrder) + 1)
    }

    function changePrice(operation) {
        if (operation === "-") {
            if (editPriceOrder > 1) setEditPriceOrder((editPriceOrder - 1).toFixed(2))
            if (editPriceOrder <= 1) setEditPriceOrder((0.01).toFixed(2))
        }
        else if (operation === "+") setEditPriceOrder((parseFloat(editPriceOrder) + 1).toFixed(2))
    }

    const handleEdit = async (modificaOrdine) => {
        const ordine_modificato = {}
        ordine_modificato.orderProductId = modificaOrdine.id
        ordine_modificato.quantity = editQtyOrder
        ordine_modificato.unitPrice = parseFloat(editPriceOrder)
        patchProdottoComanda(modificaOrdine.orderId, ordine_modificato, token, tenant)
    };

    const handleDeletePackage = async (orderId, orderPackage) => {
        try {
            const normalizedOrderId = Number(orderId);
            const normalizedOrderPackageId = Number(orderPackage?.orderPackageId ?? orderPackage?.id);
            const normalizedPackageId = Number(orderPackage?.packageId ?? orderPackage?.package?.id);
            const payload = { quantity: 0 };

            if (!Number.isInteger(normalizedOrderId) || normalizedOrderId <= 0) {
                throw new Error("Order id non valido");
            }

            if (Number.isInteger(normalizedOrderPackageId) && normalizedOrderPackageId > 0) {
                payload.orderPackageId = normalizedOrderPackageId;
            }
            if (Number.isInteger(normalizedPackageId) && normalizedPackageId > 0) {
                payload.packageId = normalizedPackageId;
            }
            if (!payload.orderPackageId && !payload.packageId) {
                throw new Error("Pacchetto non valido");
            }

            const currentOrder = tavoloFiltrato.find((order) => Number(order.id) === normalizedOrderId);
            const remainingPackages = (Array.isArray(currentOrder?.orderPackages) ? currentOrder.orderPackages : [])
                .filter((row) => {
                    const rowId = Number(row?.orderPackageId ?? row?.id);
                    if (Number.isInteger(normalizedOrderPackageId) && normalizedOrderPackageId > 0) {
                        return rowId !== normalizedOrderPackageId;
                    }
                    return Number(row?.packageId ?? row?.package?.id) !== normalizedPackageId;
                })
                .filter((row) => Number(row?.quantity || 0) > 0);
            const remainingOrder = {
                ...currentOrder,
                orderPackages: remainingPackages,
            };

            if (currentOrder && !hasVisibleOrderRows(remainingOrder)) {
                await eliminaComanda(normalizedOrderId, token, tenant);
                const remainingOrders = tavoloFiltrato.filter((order) => Number(order.id) !== normalizedOrderId);
                await releaseTableIfEmpty(remainingOrders);
            } else {
                await patchPacchettoComanda(normalizedOrderId, payload, token, tenant);
            }
            await fetchData();
            window.dispatchEvent(new CustomEvent('comanda-creata'));
        } catch (error) {
            handleAlert(t('orderDetails_alert.errorPatchOrder') + error, "error")
        }
    };

    function formatDate(date) {
        const d = new Date(date)
        d.setHours(0, 0, 0, 0)
        return d.toString()
    }

    function handleAlert(msg, type) {
        showSnackbar(msg, type)
    }

    const canSelectTargetCourse = typeof onSelectTargetCourse === 'function';
    const isTargetCourse = (orderId) => Number(selectedTargetCourseId) === Number(orderId);

    const hasLiveDraft = open && Array.isArray(liveDraftItems) && liveDraftItems.length > 0;
    const liveDraftTotal = hasLiveDraft
        ? liveDraftItems.reduce((sum, item) => sum + Number(item.total || 0), 0)
        : 0;
    const hasPersistedOrders = Array.isArray(tavoloFiltrato) && tavoloFiltrato.length > 0;
    const liveCourseNumber = (hasPersistedOrders ? tavoloFiltrato.length : 0) + 1;
    const selectedTargetCourseIndex = tavoloFiltrato.findIndex((order) => isTargetCourse(order.id));
    const visualLiveDraftCourses = useMemo(() => {
        const defaultCourseIndex = selectedTargetCourseIndex >= 0
            ? selectedTargetCourseIndex + 1
            : liveCourseNumber;
        const tastingCourses = buildTastingCourseRows(liveDraftItems);
        const fixedPriceItems = (Array.isArray(liveDraftItems) ? liveDraftItems : [])
            .filter((item) => item?.kind === "fixed-price-menu-selection");
        const maxTastingCourse = Math.max(0, ...tastingCourses.keys());
        const courseMap = new Map();

        const ensureCourse = (courseIndex, courseName = "") => {
            if (!courseMap.has(courseIndex)) {
                courseMap.set(courseIndex, {
                    id: `live-course-${courseIndex}`,
                    courseIndex,
                    courseName,
                    draftItems: [],
                    tastingItems: [],
                });
            } else if (!courseMap.get(courseIndex).courseName && courseName) {
                courseMap.get(courseIndex).courseName = courseName;
            }
            return courseMap.get(courseIndex);
        };

        (Array.isArray(liveDraftItems) ? liveDraftItems : []).forEach((item) => {
            if (item?.kind === "package" && item?.packageType === "TASTING_MENU") {
                getPackageSourceItems(item).forEach((pkgItem) => {
                    const course = getPackageItemCourse(pkgItem);
                    ensureCourse(course.index, course.name);
                });
                return;
            }

            if (item?.kind === "fixed-price-menu-selection") {
                ensureCourse(
                    Math.max(1, Number(item.courseIndex) || 1),
                    item.courseName || ""
                ).draftItems.push(item);
                return;
            }

            ensureCourse(defaultCourseIndex).draftItems.push(item);
        });

        tastingCourses.forEach((course, courseIndex) => {
            const visualCourse = ensureCourse(courseIndex, course.name);
            visualCourse.tastingItems = course.items;
        });

        const maxFixedPriceCourse = Math.max(
            0,
            ...fixedPriceItems.map((item) => Math.max(1, Number(item.courseIndex) || 1))
        );
        const totalCourses = Math.max(defaultCourseIndex, maxTastingCourse, maxFixedPriceCourse, ...courseMap.keys());
        for (let index = 1; index <= totalCourses; index += 1) {
            if (courseMap.has(index)) continue;
            if (tastingCourses.has(index)) ensureCourse(index, tastingCourses.get(index).name);
        }

        return [...courseMap.values()]
            .filter((course) => course.draftItems.length > 0 || course.tastingItems.length > 0)
            .sort((a, b) => a.courseIndex - b.courseIndex);
    }, [liveDraftItems, liveCourseNumber, selectedTargetCourseIndex]);

    const showLiveDraftOnly = !tavolo && hasLiveDraft;

    return (
        <Box
            sx={{
                width: { xs: '100%', lg: '100%' },
                height: { xs: 'auto', lg: '100%' },
                minHeight: { xs: 120, lg: 0 },
                px: { xs: 0.75, md: 2.5 },
                pt: { xs: 0.75, md: 2.5 },
                pb: { xs: 0.75, md: 1 },
                ml: { xs: 0, lg: 0 },
                mt: { xs: 0, lg: 0 },
                mb: 0,
                borderRadius: '8px',
                border: theme.palette.accordion.border,
                backgroundColor: theme.pannelloDettagli.backgroundColor,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0,
                overflow: { xs: 'visible', lg: 'hidden' },
            }}
        >
            <Box flexGrow={1} paddingRight={"6px"} sx={{
                minHeight: 0,
                maxHeight: { xs: mobileExpanded ? 'none' : '24dvh', lg: 'none' },
                overflowY: 'auto',
                backgroundColor: theme.pannelloDettagli.backgroundColor
            }} >
                {tavolo || showLiveDraftOnly ? (
                    <Box sx={{ mb: 2 }}>
                        {hasLiveDraft && visualLiveDraftCourses.map((course, courseArrayIndex) => (
                            <Accordion
                                key={course.id}
                                variant="outlined"
                                expanded
                                sx={{
                                    borderRadius: 2,
                                    marginBottom: { xs: 0.75, md: 1 },
                                    minHeight: { xs: 54, md: '74px' },
                                    alignItems: "center",
                                    backgroundColor: theme.palette.accordion.backgroundColor,
                                    border: theme.palette.accordion.border
                                }}
                            >
                                <AccordionSummary>
                                    <Box paddingY={{ xs: 0.5, md: 1.5 }} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <Typography>{course.courseIndex} - {course.courseName || t("tableMapPage.course")}</Typography>
                                        <Typography sx={{ display: "flex", alignItems: "center", gap: 0.5, color: theme.palette.accordion.color }}>
                                            {t("tableMapPage.close").toUpperCase()}<ExpandLessIcon />
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {course.draftItems.map((item) => (
                                        <Box
                                            key={item.id}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: { xs: isRestaurantTenant ? 'column' : 'row', sm: 'row' },
                                                justifyContent: 'space-between',
                                                gap: { xs: 0.75, sm: 1 },
                                                mb: 1,
                                                p: { xs: 1, md: 2 },
                                                pt: { xs: 1, md: 2 },
                                                pb: { xs: 1, md: 2 },
                                                border: theme.palette.accordion.border,
                                                borderRadius: '4px',
                                                backgroundColor: theme.palette.background.default,
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" pt={'1px'} pb={'1px'} fontWeight={"bold"} sx={{ display: { xs: isRestaurantTenant ? 'none' : 'block', sm: 'block' } }}>
                                                    {item.categoryLabel}
                                                </Typography>
                                                <Typography variant="body2" pt={'1px'} pb={'1px'} fontStyle={"italic"}>
                                                    {`(x${item.quantityLabel || item.quantity})${item.name}`}
                                                </Typography>
                                                {item.variantsLabel && (
                                                    <Typography variant="caption" color="primary" sx={{ display: 'block', fontWeight: 800 }}>
                                                        Varianti: {item.variantsLabel}
                                                    </Typography>
                                                )}
                                                {item.detailsLabel && item.packageType !== "TASTING_MENU" ? (
                                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                        {item.detailsLabel}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                            <Box alignContent={"center"} sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'space-between' }, alignItems: 'center', gap: { xs: 0.25, sm: 0 } }}>
                                                <Box sx={{ display: { xs: isRestaurantTenant ? 'flex' : 'none', sm: 'none' }, alignItems: 'center' }}>
                                                    <IconButton
                                                        size="small"
                                                        disabled={Number(item.quantity || 0) <= (item.isWeight ? 0.1 : 1)}
                                                        onClick={() => onDecrementLiveDraftItem?.(item)}
                                                        sx={{ color: 'primary.main' }}
                                                    >
                                                        <RemoveRoundedIcon />
                                                    </IconButton>
                                                    <Typography sx={{ minWidth: 26, textAlign: 'center', fontWeight: 900 }}>
                                                        {item.quantityLabel || item.quantity}
                                                    </Typography>
                                                    <IconButton size="small" onClick={() => onIncrementLiveDraftItem?.(item)} sx={{ color: 'primary.main' }}>
                                                        <AddRoundedIcon />
                                                    </IconButton>
                                                </Box>
                                                <Typography variant="body2" padding={1} fontWeight={"bold"} alignContent={"center"}>
                                                    {item.priceLabel || `€ ${Number(item.total || 0).toFixed(2)}`}
                                                </Typography>
                                                <IconButton size='small' aria-label={`Note ${item.name}`} onClick={() => onEditLiveDraftNote?.(item)}>
                                                    <Notes />
                                                </IconButton>
                                                <IconButton
                                                    size='small'
                                                    aria-label={`Varianti ${item.name}`}
                                                    disabled={!item.hasVariants || item.isWeight}
                                                    onClick={() => onEditLiveDraftVariants?.(item)}
                                                >
                                                    <ModeEditOutlinedIcon />
                                                </IconButton>
                                                <IconButton
                                                    size='small'
                                                    color="error"
                                                    onClick={() => onDeleteLiveDraftItem?.(item)}
                                                >
                                                    <DeleteOutlineOutlinedIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    ))}
                                    {course.tastingItems.map((item, itemIndex) => (
                                        <Box
                                            key={`live-tasting-${course.courseIndex}-${item.name}-${item.variantName}-${itemIndex}`}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                mb: 1,
                                                p: 2,
                                                border: `1px dashed ${theme.palette.primary.main}`,
                                                borderRadius: '4px',
                                                backgroundColor: theme.palette.background.default,
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" fontWeight={"bold"}>
                                                    {t("orderPackages.tastingMenu")} - {course.courseName || `${t("tableMapPage.course")} ${course.courseIndex}`}
                                                </Typography>
                                                <Typography variant="body2" fontStyle={"italic"}>
                                                    (x{item.quantity}){item.name}
                                                    {item.variantName ? ` + ${item.variantName}` : ""}
                                                </Typography>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                    {item.packageNames.join(", ")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                    {courseArrayIndex === 0 ? (
                                        <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.15)", mt: 0.8, pt: 0.8, display: "flex", justifyContent: "space-between" }}>
                                            <Typography sx={{ fontWeight: 700 }}>{t("total")}</Typography>
                                            <Typography sx={{ fontWeight: 700 }}>€{liveDraftTotal.toFixed(2)}</Typography>
                                        </Box>
                                    ) : null}
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        {tavolo && (
                            <>
                                {tavolo.status === "LIBERO" && hasPersistedOrders ? (
                                    <>
                                        <Box sx={{ height: 8 }} />
                                        <Box
                                            sx={{
                                                borderRadius: 1,
                                                marginTop: 1,
                                                minHeight: "74px",
                                                display: "flex",
                                                justifyContent: "center",
                                                border: theme.palette.accordion.border,
                                                backgroundColor: theme.palette.accordion.backgroundColor
                                            }}>
                                            <Typography variant="h5" color="text.secondary" sx={{ alignContent: "center" }}>
                                                {t('reservations_details.noOrder')}
                                            </Typography>
                                        </Box>
                                    </>
                                ) : hasPersistedOrders ? (
                                    <>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color={trasferisciMode ? "error" : "primary"}
                                                onClick={() => {
                                                    setTrasferisciMode(prev => !prev);
                                                    setSelectedProductIds(new Set());
                                                }}
                                            >
                                                {trasferisciMode ? t('cancel') : t('miscUi.transferItems')}
                                            </Button>
                                            {trasferisciMode && selectedProductIds.size > 0 && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => setOpenTrasferisci(true)}
                                                >
                                                    {selectedProductIds.size === 1
                                                        ? t('miscUi.moveSelectedProduct', { count: selectedProductIds.size })
                                                        : t('miscUi.moveSelectedProducts', { count: selectedProductIds.size })}
                                                </Button>
                                            )}
                                        </Box>

                                        {listaOrdiniFiltrati(tavoloFiltrato)}

                                        <Dialog open={openDelete}>
                                            <DialogTitle>{t('confirmOperation.deleteProduct')}</DialogTitle>
                                            <DialogActions>
                                                <Button variant="outlined" onClick={() => {
                                                    setOpenDelete(false)
                                                    setIdToDelete(null)
                                                    setSelectedOrder(null)
                                                }}>
                                                    {t('confirmOperation.cancel')}
                                                </Button>
                                                <Button variant="contained" onClick={() => {
                                                    handleDelete(tavoloFiltrato)
                                                    setOpenDelete(false)
                                                }}>
                                                    {t('confirmOperation.confirm')}
                                                </Button>
                                            </DialogActions>
                                        </Dialog>

                                        <Dialog open={openEdit} onClose={() => { closeEditOrder() }}>
                                            <DialogTitle>{t('confirmOperation.editOrder')}</DialogTitle>
                                            <DialogContent>
                                                <Box display={"flex"} sx={{ my: 1 }}>
                                                    <Button variant="contained" sx={{ borderRadius: 0 }} onClick={() => { changeQty("-") }}>
                                                        <Typography fontSize={"18px"}>-</Typography>
                                                    </Button>
                                                    <TextField
                                                        label={t("Quantità")}
                                                        type="number"
                                                        value={editQtyOrder}
                                                        onChange={handleChangeQty}
                                                        inputProps={{ inputMode: "decimal" }}
                                                        sx={{ borderRadius: 0, textAlignLast: "center" }}
                                                    />
                                                    <Button variant="contained" sx={{ borderRadius: 0 }} onClick={() => { changeQty("+") }}>
                                                        <Typography fontSize={"18px"}>+</Typography>
                                                    </Button>
                                                </Box>
                                                <Box display={"flex"} sx={{ my: 1 }}>
                                                    <Button variant="contained" sx={{ borderRadius: 0 }} onClick={() => { changePrice("-") }}>
                                                        <Typography fontSize={"12px"}>-1,00</Typography>
                                                    </Button>
                                                    <TextField
                                                        label={t("Prezzo")}
                                                        type="number"
                                                        value={editPriceOrder}
                                                        onChange={handleChangePrice}
                                                        inputProps={{ inputMode: "decimal" }}
                                                        sx={{ borderRadius: 0, textAlignLast: "center" }}
                                                    />
                                                    <Button variant="contained" sx={{ borderRadius: 0 }} onClick={() => { changePrice("+") }}>
                                                        <Typography fontSize={"12px"}>+1,00</Typography>
                                                    </Button>
                                                </Box>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button variant="outlined" onClick={() => { closeEditOrder() }}>
                                                    {t('confirmOperation.cancel')}
                                                </Button>
                                                <Button variant="contained" onClick={() => {
                                                    handleEdit(editOrder)
                                                    closeEditOrder()
                                                }}>
                                                    {t('confirmOperation.confirm')}
                                                </Button>
                                            </DialogActions>
                                        </Dialog>

                                        <Dialog open={openTrasferisci} onClose={() => setOpenTrasferisci(false)} maxWidth="xs" fullWidth>
                                            <DialogTitle>{t('miscUi.transferToAnotherTable')}</DialogTitle>
                                            <DialogContent>
                                                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                                    <InputLabel>{t('miscUi.destinationTable')}</InputLabel>
                                                    <Select
                                                        value={destTableId}
                                                        onChange={e => setDestTableId(e.target.value)}
                                                        label={t('miscUi.destinationTable')}
                                                    >
                                                        {tavoli
                                                            .filter(tb => tb.id !== tavoloId)
                                                            .map(tb => (
                                                                <MenuItem key={tb.id} value={tb.id}>{tb.name}</MenuItem>
                                                            ))
                                                        }
                                                    </Select>
                                                </FormControl>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={() => setOpenTrasferisci(false)}>{t('cancel')}</Button>
                                                <Button
                                                    onClick={handleConfirmTrasferisci}
                                                    disabled={!destTableId || isTransferring}
                                                    variant="contained"
                                                >
                                                    {isTransferring ? <CircularProgress size={20} /> : 'Conferma'}
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                        <Dialog open={openOccupiedTransferConfirm} onClose={() => setOpenOccupiedTransferConfirm(false)} maxWidth="xs" fullWidth>
                                            <DialogTitle>{t('miscUi.occupiedTableWarningTitle')}</DialogTitle>
                                            <DialogContent>
                                                <Typography>
                                                    {t('miscUi.occupiedDestinationTableConfirm')}
                                                </Typography>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={() => setOpenOccupiedTransferConfirm(false)}>{t('cancel')}</Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => {
                                                        setOpenOccupiedTransferConfirm(false);
                                                        handleTrasferisci();
                                                    }}
                                                >
                                                    {t('confirmOperation.confirm')}
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                    </>
                                ) : (
                                    !hasLiveDraft ? (
                                        <>
                                            <Box sx={{ height: 8 }} />
                                            <Box
                                                sx={{
                                                    borderRadius: 1,
                                                    marginTop: 1,
                                                    minHeight: "74px",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    border: theme.palette.accordion.border,
                                                    backgroundColor: theme.palette.accordion.backgroundColor
                                                }}>
                                                <Typography variant="h5" color="text.secondary" sx={{ alignContent: "center" }}>
                                                    {t('reservations_details.noOrder')}
                                                </Typography>
                                            </Box>
                                        </>
                                    ) : null
                                )}
                            </>
                        )}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            color: "text.secondary",
                        }}
                    >
                        <Typography variant="h6" textAlign={"center"}>
                            {t("noTableSelected")}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default DettaglioComande;
